/**
 * Rückweg aus einer Vollbild-Ansicht.
 *
 * ScanFlow läuft als Home-Bildschirm-App. Dort gibt es keine Browserleiste:
 * wer in einer Vollbild-Vorschau landet und den Schließen-Knopf nicht trifft,
 * kommt nur noch über „App beenden und neu starten" heraus (Benjamin,
 * 19.08.2026). Deshalb bekommt jede Vollbild-Ansicht drei Rückwege statt einem:
 *
 *   1. den sichtbaren Schließen-Knopf (baut die Ansicht selbst),
 *   2. die Zurück-Geste des Telefons — dieser Haken hier,
 *   3. die Esc-Taste am Schreibtisch.
 *
 * Der Trick bei (2): solange die Ansicht offen ist, liegt ein zusätzlicher
 * Eintrag im Verlauf. Die Zurück-Geste verbraucht ihn und schließt die
 * Vorschau, statt die Seite — in der Home-Bildschirm-App also die ganze App —
 * zu verlassen. Die Adresse ändert sich dabei nicht, der Router merkt nichts.
 */
import { useEffect, useRef } from "react";

export function useVollbildZurueck(offen: boolean, schliessen: () => void): void {
  // Über die Ref, damit ein bei jedem Rendern neu gebautes `schliessen` den
  // Verlaufseintrag nicht ständig neu setzt.
  const schliessenRef = useRef(schliessen);
  schliessenRef.current = schliessen;

  useEffect(() => {
    if (!offen) return;
    window.history.pushState({ vgVollbild: true }, "");
    let ueberVerlauf = false;

    const aufZurueck = () => {
      ueberVerlauf = true;
      schliessenRef.current();
    };
    const aufTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") schliessenRef.current();
    };
    window.addEventListener("popstate", aufZurueck);
    window.addEventListener("keydown", aufTaste);

    return () => {
      window.removeEventListener("popstate", aufZurueck);
      window.removeEventListener("keydown", aufTaste);
      // Wurde über den Knopf geschlossen, steht unser Eintrag noch im Verlauf.
      // Ohne dieses Aufräumen bräuchte die nächste Zurück-Geste zwei Versuche.
      if (!ueberVerlauf && (window.history.state as { vgVollbild?: boolean } | null)?.vgVollbild) {
        window.history.back();
      }
    };
  }, [offen]);
}
